// ToastNotifications.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { UserContext } from './App';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4000'
  : 'https://test3-0ar4.onrender.com';

// SVG Icons
const JackpotIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6"/>
    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
  </svg>
);

const CoinflipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);

const TipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const CONFIG = {
  success: {
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
    color: '#ffb703',
    bg: 'rgba(255, 183, 3, 0.15)',
    border: 'rgba(255, 183, 3, 0.4)',
  },
  jackpot: {
    Icon: JackpotIcon,
    color: '#ffb703',
    bg: 'rgba(255, 183, 3, 0.12)',
    border: 'rgba(255, 183, 3, 0.3)',
  },
  coinflip: {
    Icon: CoinflipIcon,
    color: '#ffb703',
    bg: 'rgba(255, 183, 3, 0.12)',
    border: 'rgba(255, 183, 3, 0.3)',
  },
  tip: {
    Icon: TipIcon,
    color: '#ffb703',
    bg: 'rgba(251, 191, 36, 0.12)',
    border: 'rgba(251, 191, 36, 0.3)',
  },
  info: {
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
    color: '#ffb703',
    bg: 'rgba(255, 183, 3, 0.12)',
    border: 'rgba(255, 183, 3, 0.3)',
  },
  error: {
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.3)',
  },
  warning: {
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    color: '#ffb703',
    bg: 'rgba(234, 179, 8, 0.12)',
    border: 'rgba(234, 179, 8, 0.3)',
  }
};

let toastIdCounter = 0;
window._addToast = null;

function Toast({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [progress, setProgress] = useState(100);

  const duration = toast.duration || 6000;

  useEffect(() => {
    const showFrame = requestAnimationFrame(() => setVisible(true));
    const auto = setTimeout(() => dismiss(), duration);

    // Progress bar
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));
    }, 50);

    return () => {
      cancelAnimationFrame(showFrame);
      clearTimeout(auto);
      clearInterval(tick);
    };
  }, []);

  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onRemove(toast.id), 320);
  };

  const cfg = CONFIG[toast.type] || CONFIG.success;
  const { Icon } = cfg;

  return (
    <div
      onClick={() => { toast.onClick?.(); dismiss(); }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        background: `linear-gradient(135deg, rgba(18, 18, 24, 0.98), rgba(25, 25, 35, 0.95))`,
        border: `1px solid ${cfg.border}`,
        borderRadius: '16px',
        padding: '16px 20px',
        width: '320px',
        cursor: toast.onClick ? 'pointer' : 'default',
        boxShadow: `0 20px 50px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05), 0 0 20px ${cfg.color}10`,
        transform: visible && !leaving ? 'translateX(0) scale(1)' : 'translateX(120%) scale(0.9)',
        opacity: visible && !leaving ? 1 : 0,
        transition: 'transform 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28), opacity 0.3s ease',
        overflow: 'hidden',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {/* Left accent bar - Glowing */}
      <div style={{
        position: 'absolute', left: 0, top: '20%', bottom: '20%', width: '4px',
        background: cfg.color, borderRadius: '0 4px 4px 0',
        boxShadow: `0 0 15px ${cfg.color}`,
      }} />

      {/* Icon Wrapper */}
      <div style={{
        width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
        background: `linear-gradient(135deg, ${cfg.color}25, ${cfg.color}10)`,
        border: `1px solid ${cfg.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: cfg.color,
      }}>
        <Icon />
      </div>

      {/* Text Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: '#ffffff', fontSize: '14px', fontWeight: '800',
          letterSpacing: '0.5px', marginBottom: '2px', textTransform: 'uppercase'
        }}>
          {toast.title}
        </div>
        <div style={{ color: '#a1a1aa', fontSize: '12px', lineHeight: 1.4, fontWeight: 500 }}>
          {toast.message}
        </div>
      </div>

      {/* Close button with hover */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        style={{
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', color: '#9ca3af',
          cursor: 'pointer', padding: '6px', flexShrink: 0, borderRadius: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s', alignSelf: 'flex-start', marginTop: '-4px'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
          e.currentTarget.style.color = '#ef4444';
          e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          e.currentTarget.style.color = '#9ca3af';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
        }}
      >
        <CloseIcon />
      </button>

      {/* Progress bar - Bottom */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: `linear-gradient(to right, transparent, ${cfg.color})`,
          boxShadow: `0 0 10px ${cfg.color}`,
          transition: 'width 0.1s linear',
        }} />
      </div>
    </div>
  );
}

export default function ToastNotifications() {
  const [toasts, setToasts] = useState([]);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const addToast = useCallback((toast) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev.slice(-3), { ...toast, id }]);
    
    if (toast.type === 'error') {
      const audio = new Audio('/error.mp3');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play blocked:', e));
    }
  }, []);

  useEffect(() => {
    window._addToast = addToast;
    return () => { window._addToast = null; };
  }, [addToast]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!user) return;

    const socket = io(BACKEND_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: { token: localStorage.getItem('token') },
    });

    socket.on('connect', () => {
      socket.emit('authenticate', { userId: user.id || user._id });
    });

    // Jackpot round countdown started
    socket.on('jackpot_status', (data) => {
      if (data.status === 'counting' && data.timeLeft >= 18 && !location.pathname.includes('/jackpot')) {
        addToast({
          type: 'jackpot',
          title: 'Jackpot Starting',
          message: `A jackpot round is starting — ${data.timeLeft}s left to join!`,
          onClick: () => navigate('/jackpot'),
          duration: 7000,
        });
      }
    });

    // Coinflip: someone joined your lobby
    socket.on('coinflip_started', (game) => {
      const myId = String(user.id || user._id);
      if (String(game.creator?.id) === myId && !location.pathname.includes('/coinflip')) {
        addToast({
          type: 'coinflip',
          title: 'Coinflip Started',
          message: `${game.joiner?.username || 'Someone'} joined your $${game.amount?.toFixed(2)} coinflip!`,
          onClick: () => navigate('/coinflip'),
          duration: 8000,
        });
      }
    });

    // Tip received
    socket.on('new_tip', (data) => {
      addToast({
        type: 'tip',
        title: 'Tip Received',
        message: `${data.fromUsername || 'Someone'} tipped you $${(data.amount || 0).toFixed(2)}`,
        duration: 10000,
      });
    });

    return () => socket.disconnect();
  }, [user, location.pathname, addToast, navigate]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      right: '20px',
      zIndex: 999999,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <div key={toast.id} style={{ pointerEvents: 'all' }}>
          <Toast toast={toast} onRemove={removeToast} />
        </div>
      ))}
    </div>
  );
}

